import {
  InfiniteData,
  QueryFunctionContext,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import moment, { Moment } from 'moment-timezone';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { DateTime, Event, EventsFilter } from '../interfaces';
import { EventsService } from '../services/events.service';
import { deepCopy } from '../utils/deepCopy';
import { hash } from '../utils/objectHash';
import { setEventQueryCache, updateInfiniteEvent } from './eventsCache';

export const useEventQuery = (eventId: number | string) => {
  const eventIdNumber = typeof eventId === 'string' ? parseInt(eventId, 10) : eventId;
  const queryKey = ['event', eventIdNumber];

  const queryFn = () => EventsService.getEvent(eventIdNumber);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!eventIdNumber,
    onSettled: (data) => {
      if (data) {
        updateInfiniteEvent(data);
      }
    },
  });
};

export const invalidateEventInList = async (eventId: number) => {
  const newEvent = await EventsService.getEvent(eventId);
  setEventQueryCache(newEvent);
};

export const useEventsQuery = (filter: EventsFilter, options: Partial<UseInfiniteQueryOptions> = {}) => {
  const fleet = useFleet();
  const { cursor, group_events, ...notCursor } = filter;
  const queryKey = ['events', { uuid: fleet.uuid, hash: hash(notCursor) }];
  const limit = 25;

  const queryFn = (context: QueryFunctionContext) => {
    const { pageParam, signal } = context;
    const filterWithPage = deepCopy(filter);

    const adjustedFilter = {
      limit,
      order: 'DESC',
      ...filterWithPage,
    };
    adjustedFilter.cursor = pageParam;

    if (group_events) {
      // When using grouped review feature, when we're loading more events, we want to skip all the events
      // that are already in the queue, so we adjust the filter to include them in skipped_event_ids.
      const eventsPages = queryClient.getQueryData<InfiniteData<Event[]> | undefined>(queryKey) ?? [];
      const events: Event[] = eventsPages.pages?.flatMap((page) => page?.events ?? []) ?? [];

      const eventIdsOfAllEventsInTheQueue = new Set(events.map((e) => e.id));
      events.forEach((e) => {
        e.sub_events?.forEach((se) => {
          eventIdsOfAllEventsInTheQueue.add(se.id);
        });
      });

      adjustedFilter.skipped_event_ids = [...(filter.skipped_event_ids ?? []), ...eventIdsOfAllEventsInTheQueue];
      adjustedFilter.order = 'ASC';

      return EventsService.getGroupedEvents(adjustedFilter, fleet, signal);
    }
    return EventsService.getEvents(adjustedFilter, fleet, signal);
  };

  return useInfiniteQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
    cacheTime: 0,
    staleTime: 0,
    ...options,
    getNextPageParam: (lastPage, _allPages) => {
      if (group_events) {
        return lastPage?.events?.length > 0;
      }
      return lastPage?.cursor;
    },
    getPreviousPageParam: (firstPage, _allPages) => firstPage?.cursor,
  });
};

// These two queries are used to navigate the list of portal list events from the viewer modal,
// (like when using arrow keys) -
export const useEventsListEvents = () => {
  const queryKey = ['eventsListEvents'];

  const queryFn = () => Promise.resolve<Event[]>([]);

  return useQuery({
    queryKey,
    queryFn,
  });
};

export const useEventRouteFeedback = (eventId: number) => {
  const queryFn = () => EventsService.getRouteFeedback(eventId);
  const queryKey = ['eventRouteFeedback', eventId];
  const hasPermission = useHasPermission('events:read:route_feedback');

  return useQuery({
    queryKey,
    queryFn,
    enabled: hasPermission && !!eventId,
  });
};

export const useExpirationDateForGroups = () => {
  const queryKey = ['expirationDateForGroups'];

  const queryFn = () => Promise.resolve<Moment | null>(null);

  return useQuery({
    queryKey,
    queryFn,
    staleTime: Infinity, // never considered stale
    cacheTime: Infinity, // never garbage collected
  });
};
